import { type CaptureProp, resolveCaptureProp } from "@suse-o11y/analytics/capture-prop";
import { cx } from "@suse-o11y/class-variance";
import React, { type FC } from "react";
import { useSelector } from "react-redux";

import { selectDocLinkUrlPrefix } from "../../../app/state/config/ConfigSelectors";
import { Link, type LinkProps } from "../link";

import { type DocLinkSlug, doCreateDocLinkUrl } from "./AllDocLinkSlugs";

export interface DocLinkProps extends Omit<LinkProps<"a">, "to" | "target"> {
	slug: DocLinkSlug;
	text?: string;

	/**
	 * Enforces a developer to specify a capture event OR explicitly do noting.
	 */
	capture: "noop" | CaptureProp;
}

export interface ContextLessDocLinkProps extends DocLinkProps {
	docLinkUrlPrefix: string;
}

/**
 * Creates a link to the StackState documentation.
 * To create a new link please add it to the `ALL_DOC_LINKS` array in `AllDocLinks.ts`.
 */
export const DocLink: FC<DocLinkProps> = ({
	className,
	slug,
	children,
	text = "",
	theme,
	capture,
	onClick,
	...props
}) => {
	const docLinkUrlPrefix = useSelector(selectDocLinkUrlPrefix);

	return (
		<Link
			data-testid={`doclink-${slug}`}
			href={doCreateDocLinkUrl(docLinkUrlPrefix, slug)}
			onClick={(event) => {
				if (capture !== "noop") {
					resolveCaptureProp(capture);
				}

				onClick?.(event);
			}}
			target="_blank"
			theme={theme ?? "secondary"}
			{...props}
			className={cx(!theme && "text-inherit", className)}
		>
			{text} {children}
		</Link>
	);
};

/**
 * Styled DocLink to display in tooltips
 */
export const TooltipDocLink: FC<DocLinkProps> = (props) => (
	<DocLink
		onClick={(event) => event.stopPropagation()}
		{...props}
		className="hocus:text-white underline hocus:no-underline visited:text-white visited:underline"
	/>
);
